import GlobalStyles from "@mui/material/GlobalStyles";
import * as I from "core/api/coreApiClient";
import { displayNotifications } from "core/components/App/Notifier/groupMessagesBySeverity.tsx";
import { type DisplayedSnacks, hasNewSnacks } from "core/components/App/Notifier/utils.ts";
import { clearedNotification, removeNotification } from "core/redux/actions/notification/notificationActions";
import { useAppDispatch, useAppSelector } from "core/redux/store/configureStore";
import { addSeconds } from "date-fns";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { ToastOptions } from "react-toastify";
import { Slide, toast } from "react-toastify";
import { NotificationContainer } from "widgets";

const SEQUENCE_VIEWER_QUEUE_CONTAINER_ID = "sequence-viewer-queue";

function Notifier() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const lastPathname = useRef(location.pathname);

  const displayed = useRef<DisplayedSnacks>({});

  const notifications = useAppSelector((state) => state.notification.stack);
  const clear = useAppSelector((state) => state.notification.clear);

  const storeDisplayed = (id: number, snackbarUIId: number) => {
    displayed.current[id] = snackbarUIId;
  };

  const createNotifications = useCallback(() => {
    notifications.forEach((notification) => {
      if (!notification.key) return;
      // Do nothing if snackbar is already displayed
      if (displayed.current[notification.key]) return;

      const snackbarUIId = Date.now();

      // Persist notifications: they are either dismissed manually or when the page changes, or...
      const opts: ToastOptions = {
        toastId: snackbarUIId,
        icon: () => null,
        closeButton: false,
        hideProgressBar: true,
        containerId: SEQUENCE_VIEWER_QUEUE_CONTAINER_ID,
        transition: Slide,
        onClose: () => {
          if (notification.key) {
            closeSnackbar(displayed.current[notification.key]);
          }
        },
      };

      // ...or when there is just one info message
      if (notification.messages.length === 1 && notification.messages[0].severity === I.SeverityConstants.INFO) {
        opts.autoClose = 6000;
      }

      displayNotifications(notification, { maxShown: 15 }, opts);

      // Keep track of snackbars that we've displayed
      storeDisplayed(notification.key, snackbarUIId);
      // Dispatch action to remove snackbar from redux store
      dispatch(removeNotification(notification.key));
    });
  }, [dispatch, notifications]);

  const closeSnackbar = (id?: number) => {
    toast.dismiss(id);
  };

  // Clear all notifications when the page changes
  // but only notifications that are older than 4 seconds
  // Notifications can ALL be cleared using the clearNotifications action
  const clearSnackbars = useCallback(
    (clearAll: boolean) => {
      if (clearAll) {
        closeSnackbar();
        dispatch(clearedNotification());
      } else {
        Object.values(displayed.current).forEach((id) => {
          const dateLimit = addSeconds(new Date(), -4);
          if (dateLimit > new Date(id)) {
            closeSnackbar(id);
          }
        });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const shouldClearSnackbars =
      clear || (Object.keys(displayed).length > 0 && location.pathname !== lastPathname.current);

    lastPathname.current = location.pathname;

    if (shouldClearSnackbars) {
      clearSnackbars(clear);
    }

    if (hasNewSnacks(displayed.current, notifications)) {
      createNotifications();
    }
  }, [clear, clearSnackbars, createNotifications, displayed, location.pathname, notifications]);

  return (
    <>
      <GlobalStyles
        styles={{
          "@media print": {
            ".Toastify__toast--info": {
              display: "none !important",
            },
          },
        }}
      />

      <NotificationContainer containerId={SEQUENCE_VIEWER_QUEUE_CONTAINER_ID} />
    </>
  );
}
export default Notifier;
